import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Save, FileText, CheckCircle, Users, Plus, X, UploadCloud, File, AlertCircle 
} from 'lucide-react';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../common/Toast';
import Select from 'react-select';
import '../../styles/project-requirements.css'; // Premium Wow-Factor CSS

const ProjectRequirementWorkspace = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { success, error } = useToast();

  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [tenantUsers, setTenantUsers] = useState([]);

  const [details, setDetails] = useState({
    projectTitle: '',
    projectDescription: '',
    category: '',
    estimatedBudget: '',
    targetGoLiveDate: ''
  });

  const [newRequirement, setNewRequirement] = useState({ description: '', priority: 'Must-Have' });
  const [uploadFile, setUploadFile] = useState(null);

  useEffect(() => {
    fetchProject(true);
    fetchUsers();

    const interval = setInterval(() => {
      fetchProject(false); 
    }, 5000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchProject = async (showLoading = false) => {
    try {
      if (showLoading) setLoading(true);
      const res = await api.get(`/sourcing/project-requirements/${id}`);
      if (res.data.success) {
        setProject(res.data.data);
        if (showLoading) {
          setDetails({
            projectTitle: res.data.data.projectTitle || '',
            projectDescription: res.data.data.projectDescription || '',
            category: res.data.data.category || '',
            estimatedBudget: res.data.data.estimatedBudget || '',
            targetGoLiveDate: res.data.data.targetGoLiveDate ? new Date(res.data.data.targetGoLiveDate).toISOString().split('T')[0] : ''
          });
        }
      }
    } catch (err) {
      if (showLoading) {
        console.error(err);
        error('Failed to load project.');
        navigate('/sourcing/requirement-gathering/project');
      }
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await api.get('/users'); 
      if (res.data.success && res.data.data) {
        setTenantUsers(res.data.data.map(u => ({ value: u.id || u._id, label: `${u.firstName || u.name || ''} ${u.lastName || ''}`.trim() })));
      }
    } catch (err) {
      console.warn('Could not fetch users', err);
    }
  };

  const isOwner = project?.owner?._id === user?.id || project?.owner?._id === user?._id;
  const isClosed = project?.status === 'Closed';

  const handleSaveDetails = async () => {
    if (!isOwner) return error('Only the owner can update project details.');
    try {
      setSaving(true);
      const res = await api.put(`/sourcing/project-requirements/${id}`, details);
      if (res.data.success) {
        setProject(res.data.data);
        success('Project details saved.');
      }
    } catch (err) {
      error('Failed to save project details.');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateCollaborators = async (selectedOptions) => {
    if (!isOwner) return;
    const collabs = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
    try {
      const res = await api.put(`/sourcing/project-requirements/${id}/collaborators`, { collaborators: collabs });
      if (res.data.success) {
        setProject({ ...project, collaborators: res.data.data });
        success('Collaborators updated.');
      }
    } catch (err) {
      error('Failed to update collaborators.');
    }
  };

  const handleAddRequirement = async () => {
    if (!newRequirement.description.trim()) return error('Description is required.');
    try {
      const res = await api.post(`/sourcing/project-requirements/${id}/requirements`, newRequirement);
      if (res.data.success) {
        setProject({ ...project, requirements: res.data.data });
        setNewRequirement({ description: '', priority: 'Must-Have' });
        success('Requirement added.');
      }
    } catch (err) {
      error('Failed to add requirement.');
    }
  };

  const handleRemoveRequirement = async (reqId) => {
    if (!window.confirm('Are you sure you want to delete this requirement?')) return;
    try {
      const res = await api.delete(`/sourcing/project-requirements/${id}/requirements/${reqId}`);
      if (res.data.success) {
        setProject({ ...project, requirements: res.data.data });
        success('Requirement removed.');
      }
    } catch (err) {
      error('Failed to remove requirement.');
    }
  };

  const handleFileUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile) return error('Please select a file to upload.');
    
    const formData = new FormData();
    formData.append('file', uploadFile);

    try {
      const res = await api.post(`/sourcing/project-requirements/${id}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (res.data.success) {
        setProject({ ...project, documents: res.data.data });
        setUploadFile(null);
        success('Document uploaded.');
      }
    } catch (err) {
      error('Failed to upload document.');
    }
  };

  const handleCloseProject = async () => {
    if (!window.confirm('Are you sure you want to close this project? No further edits will be allowed.')) return;
    try {
      const res = await api.post(`/sourcing/project-requirements/${id}/close`);
      if (res.data.success) {
        setProject({ ...project, status: 'Closed' });
        success('Project closed successfully. Ready for Pre-RFP phase.');
      }
    } catch (err) {
      error('Failed to close project.');
    }
  };

  if (loading) return (
    <div className="d-flex justify-content-center align-items-center min-vh-100 pr-workspace-container">
      <div className="spinner-grow text-primary" style={{width: '3rem', height: '3rem'}} role="status"></div>
    </div>
  );
  
  if (!project) return null;

  const currentCollaboratorIds = project.collaborators.map(c => c._id || c.id);
  const ownerId = project.owner?._id || project.owner?.id;
  const collaboratorOptions = tenantUsers.filter(u => u.value !== ownerId);
  const selectedCollaborators = collaboratorOptions.filter(opt => currentCollaboratorIds.includes(opt.value));

  return (
    <div className="pr-workspace-container">
      {/* Premium Glassmorphic Header */}
      <div className="pr-header">
        <div className="d-flex justify-content-between align-items-center" style={{ maxWidth: '1300px', margin: '0 auto' }}>
          <div className="d-flex align-items-center gap-4">
            <button className="pr-btn-outline pr-circle-btn" onClick={() => navigate('/sourcing/requirement-gathering/project')}>
              <ArrowLeft size={20} color="#0f172a" />
            </button>
            <div>
              <h4 className="m-0 fw-bold" style={{color: '#0f172a'}}>{project.projectTitle}</h4>
              <div className="d-flex align-items-center gap-2 mt-1">
                <span className={`pr-badge ${
                  project.status === 'Draft' ? 'pr-badge-draft' : 
                  project.status === 'In Collaboration' ? 'pr-badge-collab' : 'pr-badge-closed'
                }`}>{project.status}</span>
                <span className="text-muted small fw-medium">Project Workspace</span>
              </div>
            </div>
          </div>
          <div className="d-flex gap-3">
            {!isClosed && isOwner && (
              <>
                <button className="pr-btn-outline d-flex align-items-center gap-2" onClick={handleSaveDetails} disabled={saving}>
                  <Save size={18} /> {saving ? 'Saving...' : 'Save Details'}
                </button>
                <button className="pr-btn-success d-flex align-items-center gap-2" onClick={handleCloseProject}>
                  <CheckCircle size={18} /> Close Project
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="pr-grid-container">
        {/* Main Content Area */}
        <div className="d-flex flex-column gap-4">
          
          {isClosed && (
            <div className="pr-card p-4 d-flex align-items-center gap-3" style={{ borderLeft: '5px solid #10b981', animation: 'fadeInSlideUp 0.3s ease' }}>
              <div className="bg-success text-white p-2 rounded-circle"><CheckCircle size={24} /></div>
              <div>
                <h6 className="fw-bold mb-1">Project Closed</h6>
                <p className="mb-0 text-muted small">This workspace is locked and ready for formal sourcing and contracting phases.</p>
              </div>
            </div>
          )}

          {/* Project Details Card */}
          <div className="pr-card">
            <div className="pr-card-header">
              <div className="pr-card-title"><FileText size={20} color="#4f46e5" /> Project Overview</div>
            </div>
            <div className="pr-card-body">
              <div className="row g-4">
                <div className="col-12">
                  <div className="pr-label">Project Title</div>
                  <input 
                    type="text" 
                    className="pr-input w-100" 
                    value={details.projectTitle}
                    onChange={(e) => setDetails({...details, projectTitle: e.target.value})}
                    disabled={!isOwner || isClosed}
                  />
                </div>
                <div className="col-md-6">
                  <div className="pr-label">Category</div>
                  <select 
                    className="pr-input w-100"
                    value={details.category}
                    onChange={(e) => setDetails({...details, category: e.target.value})}
                    disabled={!isOwner || isClosed}
                  >
                    <option value="">Select Category</option>
                    <option value="IT Software">IT Software</option>
                    <option value="Construction">Construction</option>
                    <option value="Professional Services">Professional Services</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="col-md-3">
                  <div className="pr-label">Est. Budget ($)</div>
                  <input 
                    type="number" 
                    className="pr-input w-100" 
                    value={details.estimatedBudget}
                    onChange={(e) => setDetails({...details, estimatedBudget: e.target.value})}
                    disabled={!isOwner || isClosed}
                  />
                </div>
                <div className="col-md-3">
                  <div className="pr-label">Target Go-Live</div>
                  <input 
                    type="date" 
                    className="pr-input w-100" 
                    value={details.targetGoLiveDate}
                    onChange={(e) => setDetails({...details, targetGoLiveDate: e.target.value})}
                    disabled={!isOwner || isClosed}
                  />
                </div>
                <div className="col-12">
                  <div className="pr-label">Project Description</div>
                  <textarea 
                    className="pr-input w-100" 
                    rows="4"
                    value={details.projectDescription}
                    onChange={(e) => setDetails({...details, projectDescription: e.target.value})}
                    disabled={!isOwner || isClosed}
                    placeholder="Describe the overall goals, current challenges, and desired outcomes..."
                  ></textarea>
                </div>
              </div>
            </div>
          </div>

          {/* Specific Requirements Card */}
          <div className="pr-card">
            <div className="pr-card-header">
              <div className="pr-card-title">Requirement Specifications</div>
              <span className="pr-badge pr-badge-collab">{project.requirements.length} Items</span>
            </div>
            <div className="pr-card-body">
              {!isClosed && (
                <div className="d-flex flex-wrap flex-md-nowrap gap-3 mb-4 p-3 bg-light rounded" style={{border: '1px solid #e2e8f0'}}>
                  <input 
                    type="text" 
                    className="pr-input flex-grow-1" 
                    placeholder="Describe a specific requirement..." 
                    value={newRequirement.description}
                    onChange={(e) => setNewRequirement({...newRequirement, description: e.target.value})}
                  />
                  <select 
                    className="pr-input"
                    style={{minWidth: '150px'}}
                    value={newRequirement.priority}
                    onChange={(e) => setNewRequirement({...newRequirement, priority: e.target.value})}
                  >
                    <option value="Must-Have">Must-Have</option>
                    <option value="Nice-to-Have">Nice-to-Have</option>
                  </select>
                  <button className="pr-btn-primary d-flex align-items-center gap-2" onClick={handleAddRequirement}>
                    <Plus size={18} /> Add
                  </button>
                </div>
              )}

              {project.requirements.length === 0 ? (
                <div className="text-center py-5">
                  <div className="text-muted fw-medium">No requirements specified yet.</div>
                  <p className="small text-muted mt-2">Add requirements above to start building the specification list.</p>
                </div>
              ) : (
                <div className="d-flex flex-column gap-2">
                  {project.requirements.map(req => (
                    <div key={req._id} className="pr-list-item d-flex justify-content-between align-items-center">
                      <div>
                        <div className="fw-bold text-dark">{req.description}</div>
                        <div className="text-muted small mt-1">
                          Added by {req.addedBy ? `${req.addedBy.firstName || ''} ${req.addedBy.lastName || ''}`.trim() : 'Unknown'} • {new Date(req.addedAt).toLocaleDateString()}
                        </div>
                      </div>
                      <div className="d-flex align-items-center gap-4">
                        <span className={`pr-badge ${req.priority === 'Must-Have' ? 'pr-badge-must' : 'pr-badge-nice'}`}>
                          {req.priority}
                        </span>
                        {!isClosed && (isOwner || req.addedBy?._id === user?._id || req.addedBy?._id === user?.id) && (
                          <button className="btn btn-sm text-danger p-2 rounded-circle" style={{background: '#fee2e2'}} onClick={() => handleRemoveRequirement(req._id)} title="Remove">
                            <X size={16} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Documents Section */}
          <div className="pr-card">
            <div className="pr-card-header">
              <div className="pr-card-title">Supporting Documents</div>
            </div>
            <div className="pr-card-body">
              {!isClosed && (
                <form onSubmit={handleFileUpload} className="mb-4">
                  <label className="pr-upload-zone w-100 d-block">
                    <UploadCloud size={40} className="icon mb-3" />
                    <h6 className="fw-bold mb-2">Drag & Drop or Click to Upload</h6>
                    <p className="text-muted small mb-3">Upload process maps, business specifications, or legacy contracts.</p>
                    <div className="d-flex justify-content-center align-items-center gap-3">
                      <input 
                        type="file" 
                        className="pr-input bg-white" 
                        style={{maxWidth: '300px'}}
                        onChange={(e) => setUploadFile(e.target.files[0])}
                      />
                      <button type="submit" className="pr-btn-primary px-4" disabled={!uploadFile}>Upload</button>
                    </div>
                  </label>
                </form>
              )}

              {project.documents.length === 0 ? (
                <div className="text-center py-4">
                  <div className="text-muted fw-medium">No documents attached.</div>
                </div>
              ) : (
                <div className="row g-3">
                  {project.documents.map(doc => (
                    <div key={doc._id} className="col-md-6">
                      <div className="pr-list-item d-flex align-items-center gap-3 m-0">
                        <div className="pr-avatar pr-avatar-owner" style={{background: '#e0e7ff', color: '#4f46e5'}}>
                          <File size={18} />
                        </div>
                        <div className="flex-grow-1 overflow-hidden">
                          <div className="fw-bold text-truncate" title={doc.fileName}>{doc.fileName}</div>
                          <div className="text-muted small mt-1">Uploaded by {doc.uploadedBy ? `${doc.uploadedBy.firstName || ''} ${doc.uploadedBy.lastName || ''}`.trim() : 'Unknown'}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

        </div>

        {/* Right Sidebar */}
        <div className="d-flex flex-column gap-4">
          
          {/* Tracking Widget */}
          <div className="pr-status-card">
            <div className="d-flex align-items-center gap-3 mb-3">
              <div className="bg-white text-primary p-2 rounded-circle" style={{opacity: 0.9}}>
                <AlertCircle size={22} />
              </div>
              <h6 className="fw-bold text-white m-0" style={{zIndex: 2}}>Lifecycle Sync</h6>
            </div>
            <p className="small text-white mb-0" style={{opacity: 0.9, lineHeight: 1.6, zIndex: 2, position: 'relative'}}>
              This workspace allows your team to collaborate on requirements before formal procurement. 
              Once all items are gathered, the owner can Close the workspace, converting it to an official Pre-RFP pipeline record.
            </p>
          </div>

          {/* Collaborators Card */}
          <div className="pr-card">
            <div className="pr-card-header">
              <div className="pr-card-title"><Users size={20} color="#4f46e5" /> Team Access</div>
            </div>
            <div className="pr-card-body">
              
              {isOwner && !isClosed && (
                <div className="mb-4">
                  <div className="pr-label">Invite Collaborators</div>
                  <Select
                    isMulti
                    name="collaborators"
                    options={collaboratorOptions}
                    classNamePrefix="select"
                    value={selectedCollaborators}
                    onChange={handleUpdateCollaborators}
                    placeholder="Search users..."
                    styles={{
                      control: (base) => ({
                        ...base,
                        borderRadius: '10px',
                        border: '2px solid #e2e8f0',
                        boxShadow: 'none',
                        '&:hover': { border: '2px solid #cbd5e1' }
                      })
                    }}
                  />
                  <div className="small text-muted mt-2">Invited members can add requirements and upload documents.</div>
                </div>
              )}

              <div className="pr-label text-uppercase mt-4 mb-3">Active Members</div>
              <div className="d-flex flex-column gap-3">
                {/* Owner Item */}
                <div className="d-flex justify-content-between align-items-center bg-light p-2 rounded border">
                  <div className="d-flex align-items-center gap-3">
                    <div className="pr-avatar pr-avatar-owner">
                      {project.owner?.firstName?.charAt(0) || 'O'}
                    </div>
                    <div>
                      <div className="fw-bold" style={{fontSize: '0.9rem'}}>{project.owner ? `${project.owner.firstName || ''} ${project.owner.lastName || ''}`.trim() : 'Owner'}</div>
                      <div className="text-muted small">Owner</div>
                    </div>
                  </div>
                  <span className="pr-badge pr-badge-draft bg-white">Full Access</span>
                </div>
                
                {/* Collaborator Items */}
                {project.collaborators.map(c => (
                  <div key={c._id} className="d-flex justify-content-between align-items-center p-2 rounded" style={{border: '1px solid #f1f5f9'}}>
                    <div className="d-flex align-items-center gap-3">
                      <div className="pr-avatar pr-avatar-collab">
                        {c.firstName?.charAt(0) || 'U'}
                      </div>
                      <div>
                        <div className="fw-bold text-dark" style={{fontSize: '0.9rem'}}>{`${c.firstName || ''} ${c.lastName || ''}`.trim()}</div>
                        <div className="text-muted small">Collaborator</div>
                      </div>
                    </div>
                    <span className="pr-badge pr-badge-draft">Can Edit</span>
                  </div>
                ))}
              </div>

            </div>
          </div>
          
        </div>
      </div>
    </div>
  );
};

export default ProjectRequirementWorkspace;
